import { useState } from 'react'
import { useWorkerScoreForPeriod, useWorkerScoreHistory, useWorkerStanding } from '../index.js'
import type { ScoreInput, ScoreSnapshotSummary, WorkerScore } from '../index.js'
import { WorkerBreakdownBars } from '../components/WorkerBreakdownBars.js'
import { formatMonth, formatMonthShort } from '../components/outcomeGroups.js'
import { ScoreRing } from '../components/ScoreRing.js'
import { NoScoreRing } from '../components/NoScoreRing.js'
import { HelpIcon, TIER_LABELS, Skeleton } from '../../core/index.js'
import { LoadFailure } from '../../core/index.js'

interface MonthRow {
  /** `YYYY-MM`. */
  month: string
  kind: 'running' | 'final'
  score: number | null
  tier: WorkerScore['tier']
  provisional: boolean
}

/** The month a period starts in, as `YYYY-MM`. */
function monthOf(from: string): string {
  return from.slice(0, 7)
}

function rowsFrom(history: { period: { from: string }; running: { score: number | null; provisional: boolean }[]; finals: ScoreSnapshotSummary[] }): MonthRow[] {
  const latest = history.running[history.running.length - 1]
  const current: MonthRow = { month: monthOf(history.period.from), kind: 'running', score: latest?.score ?? null, tier: null, provisional: latest?.provisional ?? false }
  const finals = history.finals.map((final): MonthRow => ({ month: monthOf(final.from), kind: 'final', score: final.score, tier: final.tier, provisional: final.provisional }))
  // Newest first, the running month on top, a final for the same month (just closed) in its place.
  const ordered = [...finals].sort((a, b) => b.month.localeCompare(a.month))
  return ordered.some((row) => row.month === current.month) ? ordered : [current, ...ordered]
}

/** The engine's stored result as the breakdown's rows, so a past month shows the same bars as the current one. */
function inputsFromScore(score: WorkerScore): ScoreInput[] {
  return score.result.inputs.map((input) => ({
    key: input.key,
    label: input.label,
    value: input.value ?? 0,
    weight: input.weight,
    nudge: null,
    included: input.included,
    excludedReason: input.excludedReason,
    measure: input.measure,
    numerator: input.numerator,
    denominator: input.denominator,
    best: input.best,
    worst: input.worst,
    pointsLost: input.pointsLost,
  }))
}

/**
 * One dial per month, newest first, as the facility's staff see the same
 * worker: the final score in the tier it set, the running month in the
 * tier held now. Tapping a month shows that month's bars beneath the
 * grid. A month with no score is a month that kept what the worker had,
 * not a zero, so its dial is empty rather than at nought.
 */
export function WorkerHistory({ workerId }: { workerId: string }) {
  const historyQuery = useWorkerScoreHistory(workerId)
  // The tier held now colours the running month's dial; a tier only changes at a close.
  const standingQuery = useWorkerStanding(workerId)
  const [openMonth, setOpenMonth] = useState<string | null>(null)

  if (historyQuery.isPending) {
    return <Skeleton lines={5} className="p-8" />
  }
  if (historyQuery.isError) {
    return <LoadFailure className="p-8" message="Your history could not be loaded." onRetry={() => void historyQuery.refetch()} retrying={historyQuery.isFetching} />
  }

  const rows = rowsFrom(historyQuery.data)
  const heldTier = standingQuery.data?.tier ?? 'probationary'

  return (
    <div className="max-w-md mx-auto flex flex-col gap-3 p-4">
      <div className="flex items-center gap-1 text-[9px] font-bold uppercase tracking-wide text-ink-mid">Score by month<HelpIcon topic="scoring.worker-history" /></div>
      <ul className="grid grid-cols-4 gap-1.5">
        {rows.map((row) => {
          const isOpen = openMonth === row.month
          return (
            <li key={row.month}>
              <button
                type="button"
                onClick={() => setOpenMonth(isOpen ? null : row.month)}
                aria-expanded={isOpen}
                aria-label={`Show ${formatMonth(row.month)} in full`}
                className={`w-full h-full bg-white border px-1.5 py-2 text-center transition-colors hover:bg-canvas ${isOpen ? 'border-marine ring-1 ring-marine' : row.kind === 'running' ? 'border-marine/40' : 'border-marine/15'}`}
              >
                <div className="mb-1.5 text-[11px] font-bold text-ink">{formatMonthShort(row.month)}</div>
                {row.score === null ? <NoScoreRing reason="No score" /> : <ScoreRing score={row.score} tier={row.tier ?? heldTier} size="sm" />}
                <div className="mt-1.5 text-[9px] leading-tight text-ink-mid">
                  {row.kind === 'running' ? 'Running' : (row.tier && TIER_LABELS[row.tier]) || 'Final'}
                  {row.provisional && <> · prov.</>}
                </div>
              </button>
            </li>
          )
        })}
      </ul>
      {openMonth !== null && (
        <div className="bg-white border border-marine ring-1 ring-marine">
          <div className="px-3 pt-3 text-xs font-bold text-ink">{formatMonth(openMonth)}</div>
          <MonthDetail workerId={workerId} month={openMonth} />
        </div>
      )}
      <p className="text-[10px] text-ink-mid/80 leading-relaxed">Each month stands alone. A month with no shifts is not a zero: you keep what you had.</p>
    </div>
  )
}

function MonthDetail({ workerId, month }: { workerId: string; month: string }) {
  const scoreQuery = useWorkerScoreForPeriod(workerId, month)
  if (scoreQuery.isPending) {
    return <Skeleton className="p-3" />
  }
  if (scoreQuery.isError) {
    return <LoadFailure size="xs" className="p-3" message="This month could not be loaded." onRetry={() => void scoreQuery.refetch()} retrying={scoreQuery.isFetching} />
  }
  if (scoreQuery.data === null) {
    return <div className="p-3 text-[11px] text-ink-mid">No shifts were scored this month.</div>
  }
  return (
    <div className="p-3">
      <WorkerBreakdownBars score={scoreQuery.data} inputs={inputsFromScore(scoreQuery.data)} />
    </div>
  )
}
